/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Ref, RefObject } from "react";

import * as React from "react";
import { Dropzone } from "../Dropzone";
import styled, { css, SimpleInterpolation } from "styled-components";
import { devices, useIsMobile } from "components/shared/Styles";

import {
  EnhanceUpsellContent,
  InitialContent,
} from "components/EnhancedSpeech/FreeTierEnhanceSpeech/Upsells";
import { CompletedEnhanceSpeechTrack } from "components/EnhancedSpeech/EnhanceSpeechTrack";
import { useCurrentUser } from "actions/CurrentUserActions";

interface PlatformContentProps {
  dropZoneRef?: Ref<RefObject<HTMLDivElement>>;
  backgroundColor?: string;
  isBusy?: boolean;
  isFinished?: boolean;
  hasTrackError?: boolean;
  completedEnhanceSpeechTrack?: CompletedEnhanceSpeechTrack;
}

const DropzoneLarge = styled(Dropzone)`
  border-radius: 32px;
  width: 100%;
  height: 100%;
  min-height: 555px;
  gap: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;

  ${devices.medium} {
    min-height: 400px;
  }
`;

const DesktopContentWrapper = styled.div<{
  boxShadow?: boolean;
}>`
  border-radius: 32px;
  display: flex;
  width: 100%;
  min-height: 555px;
  background-color: rgba(
    var(--spectrum-global-color-static-indigo-600-rgb),
    0.1
  );
  outline: 8px solid
    rgba(var(--spectrum-global-color-static-indigo-600-rgb), 0.1);
  margin: 0px 8px;

  ${({ boxShadow }): SimpleInterpolation => {
    if (boxShadow) {
      return css`
        box-shadow: 0px 12px 24px 0px rgba(57, 63, 199, 0.09),
          0px 0px 2px 0px rgba(0, 2, 56, 0.15),
          0px 3px 4px 0px rgba(0, 2, 56, 0.03);
      `;
    }
  }})

  ${devices.medium} {
    min-height: 400px;
  }
`;

const MobileContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 80px;
`;

const LeftColumn = styled.div<{
  backgroundcolor?: string;
  splitColumns?: boolean;
  boxShadow?: boolean;
}>`
  display: flex;
  flex: 52;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${({ splitColumns }): string => {
    return splitColumns ? "32px" : "32px 0px 0px 32px";
  }};
  background-color: ${({ backgroundcolor }): string => backgroundcolor || ""};

  ${devices.medium} {
    padding: 32px;
  }

  ${({ boxShadow }): SimpleInterpolation => {
    if (boxShadow) {
      return css`
        box-shadow: 0px 12px 24px 0px rgba(57, 63, 199, 0.09),
          0px 0px 2px 0px rgba(0, 2, 56, 0.15),
          0px 3px 4px 0px rgba(0, 2, 56, 0.03);
      `;
    }
  }})
`;

LeftColumn.defaultProps = {
  splitColumns: true,
};

const RightColumn = styled.div<{
  splitColumns?: boolean;
}>`
  display: flex;
  flex: 48;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  border-radius: ${({ splitColumns }): string => {
    return splitColumns ? "32px" : "0px 32px 32px 0px";
  }};
  background-color: ${({ splitColumns }): string =>
    splitColumns ? "" : "var(--spectrum-global-color-static-white)"};

  ${devices.medium} {
    padding: 24px;
  }
`;

RightColumn.defaultProps = {
  splitColumns: true,
};

const DesktopContent: React.FC<PlatformContentProps> = ({
  children,
  dropZoneRef,
  backgroundColor,
  isBusy = false,
  isFinished = false,
  hasTrackError = false,
  completedEnhanceSpeechTrack,
}) => {
  const { currentUser } = useCurrentUser();

  const isPostEnhanceStudioNudgeEnabled = Boolean(
    currentUser?.postEnhanceStudioNudgeEnabled
  );

  const splitColumns =
    isBusy || hasTrackError || (isFinished && !isPostEnhanceStudioNudgeEnabled);

  if (isBusy || isFinished || hasTrackError) {
    return (
      <DesktopContentWrapper boxShadow={!splitColumns}>
        <LeftColumn
          backgroundcolor="var(--spectrum-global-color-static-white)"
          splitColumns={splitColumns}
          boxShadow={splitColumns}
        >
          {children}
        </LeftColumn>
        <RightColumn data-testid="right-column" splitColumns={splitColumns}>
          <EnhanceUpsellContent
            hasTrackError={hasTrackError}
            isBusy={isBusy}
            completedEnhanceSpeechTrack={completedEnhanceSpeechTrack}
          />
        </RightColumn>
      </DesktopContentWrapper>
    );
  } else {
    return (
      <DropzoneLarge
        data-testid="enhance-speech-preview-drop-zone"
        ref={dropZoneRef}
        dashlength={16}
        radius={32}
        color="rgb(92, 92, 224)" // "var(--spectrum-global-color-static-indigo-600)"
        backgroundcolor={
          backgroundColor || "var(--spectrum-global-color-static-white)"
        }
      >
        <LeftColumn>
          <InitialContent />
        </LeftColumn>
        <RightColumn>{children}</RightColumn>
      </DropzoneLarge>
    );
  }
};

const MobileContent: React.FC<PlatformContentProps> = ({
  children,
  dropZoneRef,
  backgroundColor = "none",
  isBusy = false,
  isFinished = false,
  hasTrackError = false,
}) => {
  if (isBusy || isFinished || hasTrackError) {
    return (
      <MobileContentWrapper>
        <LeftColumn>{children}</LeftColumn>
        <LeftColumn>
          <EnhanceUpsellContent isBusy={isBusy} hasTrackError={hasTrackError} />
        </LeftColumn>
      </MobileContentWrapper>
    );
  } else {
    return (
      <MobileContentWrapper>
        <Dropzone
          data-testid="enhance-speech-preview-drop-zone"
          ref={dropZoneRef}
          dashlength={16}
          color={"none"}
          backgroundcolor={backgroundColor}
        >
          {children}
        </Dropzone>
        <LeftColumn>
          <InitialContent />
        </LeftColumn>
      </MobileContentWrapper>
    );
  }
};

export const PlatformContent: React.FC<PlatformContentProps> = ({
  children,
  ...props
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileContent {...props}>{children}</MobileContent>;
  } else {
    return <DesktopContent {...props}>{children}</DesktopContent>;
  }
};
